import {z} from 'zod';

import {SaleVehiclePhotoTypeSchema} from '@dms/api/settings';

/**
 * One position of a Sales Album's Guide Layout. A slot carries its Photo Type and either the
 * photo that fills it or `null` when it is empty; an Extra Photo has no Photo Type.
 */
export const SaleVehicleAlbumPhotoGuideItemSchema = z.object({
  position: z.number(),
  photoType: SaleVehiclePhotoTypeSchema.nullable(),
  required: z.boolean(),
  saleVehiclePhotoId: z.string().nullable(),
});
export type SaleVehicleAlbumPhotoGuideItem = z.infer<typeof SaleVehicleAlbumPhotoGuideItemSchema>;

/**
 * The Guide Layout of a Sales Album. An album without its own layout returns the tenant's Photo
 * Guide slots with every `saleVehiclePhotoId` set to `null`. Required slots are advisory: the
 * missing list only feeds the gallery's warning counter.
 */
export const SaleVehicleAlbumPhotoGuideSchema = z.object({
  enabled: z.boolean(),
  items: z.array(SaleVehicleAlbumPhotoGuideItemSchema),
  missingRequiredPhotoTypes: z.array(SaleVehiclePhotoTypeSchema),
});
export type SaleVehicleAlbumPhotoGuide = z.infer<typeof SaleVehicleAlbumPhotoGuideSchema>;

export const SaleVehicleAlbumPhotoGuideApiArgSchema = z.object({
  saleVehicleId: z.string(),
  saleVehicleAlbumId: z.string(),
});
export type SaleVehicleAlbumPhotoGuideApiArg = z.infer<
  typeof SaleVehicleAlbumPhotoGuideApiArgSchema
>;

export const AssignSaleVehiclePhotoTypeRequestBodySchema = z.object({
  saleVehiclePhotoId: z.string(),
  photoType: SaleVehiclePhotoTypeSchema,
});
export type AssignSaleVehiclePhotoTypeRequestBody = z.infer<
  typeof AssignSaleVehiclePhotoTypeRequestBodySchema
>;
export const AssignSaleVehiclePhotoTypeApiArgSchema = SaleVehicleAlbumPhotoGuideApiArgSchema.extend(
  {
    body: AssignSaleVehiclePhotoTypeRequestBodySchema,
  }
);
export type AssignSaleVehiclePhotoTypeApiArg = z.infer<
  typeof AssignSaleVehiclePhotoTypeApiArgSchema
>;

export const UnassignSaleVehiclePhotoFromGuideRequestBodySchema = z.object({
  saleVehiclePhotoId: z.string(),
});
export type UnassignSaleVehiclePhotoFromGuideRequestBody = z.infer<
  typeof UnassignSaleVehiclePhotoFromGuideRequestBodySchema
>;
export const UnassignSaleVehiclePhotoFromGuideApiArgSchema =
  SaleVehicleAlbumPhotoGuideApiArgSchema.extend({
    body: UnassignSaleVehiclePhotoFromGuideRequestBodySchema,
  });
export type UnassignSaleVehiclePhotoFromGuideApiArg = z.infer<
  typeof UnassignSaleVehiclePhotoFromGuideApiArgSchema
>;

export const SaleVehicleAlbumPhotoGuideLayoutItemSchema = z.object({
  photoType: SaleVehiclePhotoTypeSchema.nullable(),
  saleVehiclePhotoId: z.string().nullable(),
});
export type SaleVehicleAlbumPhotoGuideLayoutItem = z.infer<
  typeof SaleVehicleAlbumPhotoGuideLayoutItemSchema
>;
export const UpdateSaleVehicleAlbumPhotoGuideLayoutRequestBodySchema = z.object({
  items: z.array(SaleVehicleAlbumPhotoGuideLayoutItemSchema),
});
export type UpdateSaleVehicleAlbumPhotoGuideLayoutRequestBody = z.infer<
  typeof UpdateSaleVehicleAlbumPhotoGuideLayoutRequestBodySchema
>;
export const UpdateSaleVehicleAlbumPhotoGuideLayoutApiArgSchema =
  SaleVehicleAlbumPhotoGuideApiArgSchema.extend({
    body: UpdateSaleVehicleAlbumPhotoGuideLayoutRequestBodySchema,
  });
export type UpdateSaleVehicleAlbumPhotoGuideLayoutApiArg = z.infer<
  typeof UpdateSaleVehicleAlbumPhotoGuideLayoutApiArgSchema
>;
